import { useEffect, useMemo, useRef } from 'react';

import { LazyQueryResult } from '@apollo/client';
import { sortBy } from 'lodash';

import {
  TemplateAttachementsQuery,
  TemplateAttachementsQueryVariables,
  useTemplateAttachementsLazyQuery,
} from 'src/generated/mloop-graphql';

import { FileStatus, FileUploadFlow, UIFile, initFiles } from 'src/slices/files';

import { useDispatch, useSelector } from 'src/store';

export type AttachmentType = {
  name: string;
  timestamp: number;
  path: string;
  size: number;
};

/** A single attachment as returned by GraphQL, where every field is nullable. */
type NullableAttachment = {
  name?: string | null;
  path?: string | null;
  size?: number | null;
  timestamp?: number | null;
};

/** Minimal shape needed to seed attachments from an already-fetched template (see useInitAttachmentsFromFilledTemplate). */
type FilledTemplateForAttachments = {
  id?: string | null;
  attachments?: NullableAttachment[] | null;
} | null;

export const useInitAttachmentsForEmailTemplate = (
  id: string | null,
  flow: FileUploadFlow
): LazyQueryResult<TemplateAttachementsQuery, TemplateAttachementsQueryVariables> => {
  const dispatch = useDispatch();
  const [fetchEmailTemplateAttachments, { data, ...rest }] = useTemplateAttachementsLazyQuery();
  const initializedTemplateIdRef = useRef<string | null>(null);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    if (!id) {
      initializedTemplateIdRef.current = null;
      return;
    }
    fetchEmailTemplateAttachments({ variables: { id } });
  }, [fetchEmailTemplateAttachments, id]);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    if (!id || !data?.template || data.template.id !== id) {
      return;
    }
    // Seed Redux from the template only once per selected template id so Apollo
    // refetches do not wipe user-added attachments that are still uploading (BUG-696).
    if (initializedTemplateIdRef.current === id) {
      return;
    }
    initializedTemplateIdRef.current = id;

    const attachments: UIFile[] =
      data.template.attachments?.map((attachment: AttachmentType) => {
        return { ...attachment, status: FileStatus.UPLOADED };
      }) || [];

    dispatch(initFiles(flow, attachments));
  }, [dispatch, flow, id, data]);

  return useMemo(
    () => ({ data, ...rest }) as LazyQueryResult<TemplateAttachementsQuery, TemplateAttachementsQueryVariables>,
    [data, rest]
  );
};

/**
 * Seeds Redux attachment state for a flow from a template's attachments that the caller has
 * already fetched via another query (e.g. useScheduleContent's ScheduleContentTemplateById, used
 * by the Slack/candidate-invite/interviewer-invite comms cards). Unlike
 * useInitAttachmentsForEmailTemplate, this does not issue its own query -- it just applies the
 * same dedup-by-template-id guard (BUG-696) so refills of filledTemplate for the *same* template
 * (e.g. from other placeholder data loading) don't wipe attachments the user is actively
 * editing/uploading. Pass undefined templateId to skip seeding entirely (e.g. attachments not
 * supported for this template type).
 */
export const useInitAttachmentsFromFilledTemplate = (
  templateId: string | undefined,
  flow: FileUploadFlow,
  filledTemplate: FilledTemplateForAttachments,
  eventId?: string
): void => {
  const dispatch = useDispatch();
  const initializedTemplateIdRef = useRef<string | undefined>(undefined);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect runs imperative data work when dependencies change; prefer a data hook when possible.
  useEffect(() => {
    if (!templateId) {
      initializedTemplateIdRef.current = undefined;
      return;
    }
    if (!filledTemplate || filledTemplate.id !== templateId) {
      return;
    }
    if (initializedTemplateIdRef.current === templateId) {
      return;
    }
    initializedTemplateIdRef.current = templateId;

    const files: UIFile[] = (filledTemplate.attachments || []).map((attachment) => ({
      name: attachment.name || '',
      timestamp: attachment.timestamp || Date.now(),
      path: attachment.path || '',
      size: attachment.size || 0,
      status: FileStatus.UPLOADED,
    }));

    dispatch(initFiles(flow, files, eventId));
  }, [dispatch, flow, templateId, filledTemplate, eventId]);
};

export const useAttachmentsForUpload = (flow: FileUploadFlow, eventId?: string): AttachmentType[] | null => {
  const fileMap = useSelector((state) => {
    if (flow === FileUploadFlow.INTERVIEWER_EVENT) {
      return eventId ? state.fileUpload[FileUploadFlow.INTERVIEWER_EVENT][eventId] : undefined;
    }
    return state.fileUpload[flow];
  });
  const files = sortBy(fileMap, 'timestamp') || [];

  const attachments: AttachmentType[] = [];
  for (let cnt = 0; cnt < files.length; cnt++) {
    const file: UIFile = files[cnt];
    if (file.status === FileStatus.UPLOADED && file.path) {
      const attachment: AttachmentType = {
        name: file.name,
        timestamp: file.timestamp,
        path: file.path,
        size: file.size,
      };
      attachments.push(attachment);
    }
  }
  return attachments.length ? attachments : null;
};
